<template>
  <div class="w-full h-full rounded-[5px] overflow-hidden mobile-only">
    <video
      :id="`videoRef-${index + 1}`"
      ref="videoRef"
      :src="src"
      class="w-full h-full opacity-0 video-js vjs-default-skin"
    ></video>
  </div>
</template>

<script setup>
import videojs from "video.js";
import { useMainStore } from "~/store";

const props = defineProps({
  src: {
    type: String,
    required: true,
    default: "",
  },
  poster: {
    type: String,
    required: true,
    default: "",
  },
  index: {
    type: Number,
    required: true,
    default: 0,
  },
});

const emit = defineEmits();
const store = useMainStore();
const nuxtApp = useNuxtApp();

const isLocked = ref(false);
const isPlaysInline = ref(false);
const videoRef = ref(null);
let player;

const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

function onModalOpened() {
  player = videojs(
    document.getElementById(`videoRef-${props.index + 1}`),
    {
      controls: true,
      autoplay: false,
      preload: "auto",
      language: store.locale,
      fluid: false,
      // fill: false,
      responsive: true,
      experimentalSvgIcons: false,
      userActions: {
        hotkeys: true,
      },
      playsinline: true,
      enableSmoothSeeking: true,
      controlBar: {
        remainingTimeDisplay: {
          displayNegative: true,
        },
        skipButtons: {
          backward: 10,
          forward: 10,
        },
      },
    },
    function () {
      document.querySelector(".video-js").classList.remove("opacity-0");
      document.querySelector(".video-js").classList.add("opacity-100");
      document.querySelector(".vjs-big-play-button").click();

      screen.orientation.addEventListener("change", onChangeScreen);

      const el = player.el();

      nuxtApp.$bus.emit("lock-mode", true);

      player.on("fullscreenchange", function () {
        if (player.isFullscreen()) {
          nuxtApp.$bus.emit("lock-mode", true);

          isLocked.value = true;
          handleVideoClick(
            document.getElementById(`videoRef-${props.index + 1}`)
          );
        } else {
          exitFullscreen();

          isLocked.value = false;
          nuxtApp.$bus.emit("lock-mode", false);
        }
      });

      const videoEl = el.querySelector("video");

      // videoEl.addEventListener("focusout", displayIcon);

      // Hide icon when container gains focus (tap inside)
      // videoEl.addEventListener("focusin", hideIcon);

      // Listen for PiP enter/leave
      videoEl.addEventListener("enterpictureinpicture", pipEnter);

      videoEl.addEventListener("leavepictureinpicture", pipExit);
    }
  );
  if (props.poster) {
    player.poster(props.poster);
  }
}

onMounted(() => {
  onModalOpened();
});

const onChangeScreen = (e) => {
  if (e?.target?.type?.includes("landscape")) {
    nuxtApp.$bus.emit("lock-mode", true);
  } else {
    nuxtApp.$bus.emit("lock-mode", false);
  }
};

// const displayIcon = () => {
//   playIcon.style.display = "block";
// };

// const hideIcon = () => {
//   playIcon.style.display = "none";
// };

const pipEnter = () => {
  // emit("update:modelValue", false);
  exitFullscreen();
};

const pipExit = () => {
  // emit("update:modelValue", true);
};

const handleVideoClick = async (video) => {
  if (!isIOS()) {
    // Try to lock orientation
    if (screen.orientation && screen.orientation.lock) {
      try {
        // portrait-primary
        await screen.orientation.lock("landscape");
      } catch (err) {
        console.warn("Orientation lock failed:", err);
      }
    }
  }
};

// Optional: unlock on fullscreen exit

const exitFullscreen = () => {
  if (!document.fullscreenElement && screen.orientation?.unlock) {
    screen.orientation.unlock();
  }
};

document.addEventListener("fullscreenchange", exitFullscreen);
onBeforeUnmount(() => {
  nuxtApp.$bus.emit("lock-mode", false);
  if (player) {
    if (player.el()) {
      const videoEl = player.el().querySelector("video");
      if (document.pictureInPictureElement === videoEl) {
        document
          .exitPictureInPicture()
          .then(() => {})
          .catch(console.error);
      }

      if (videoEl) {
        // Listen for PiP enter/leave
        videoEl.removeEventListener("enterpictureinpicture", pipEnter);
        videoEl.removeEventListener("leavepictureinpicture", pipExit);
        // videoEl.removeEventListener("focusout", displayIcon);
        // videoEl.removeEventListener("focusin", hideIcon);
      }
    }
    player.dispose();
  }
  if (screen.orientation) {
    screen.orientation.removeEventListener("change", onChangeScreen);
  }
  document.removeEventListener("fullscreenchange", exitFullscreen);
});
</script>
<style >
@import "video.js/dist/video-js.css";

.mobile-only .rotate-message {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 16px;
  z-index: 9999;
}

/* close popup */
.mobile-only .vjs-button.vjs-close-popup .vjs-icon-placeholder svg {
  @apply opacity-0;
}
.mobile-only
  .vjs-has-started.vjs-user-inactive.vjs-playing
  .vjs-button.vjs-close-popup,
.mobile-only .vjs-button.vjs-close-popup.locked {
  @apply opacity-0 pointer-events-none;
}
.mobile-only .vjs-button.vjs-close-popup {
  transition: visibility 0.1s, opacity 0.1s;
  @apply w-5 h-5 top-[27px] left-[22px] visible;
}
.mobile-only .vjs-button.vjs-close-popup .vjs-icon-placeholder {
  background-image: url("~/assets/svg/close-light.svg");
  @apply w-5 h-5 bg-contain bg-no-repeat;
}

.mobile-only .vjs-control-bar {
  @apply !bg-transparent;
}
.mobile-only
  .vjs-play-control.vjs-control.vjs-button.vjs-paused
  .vjs-icon-placeholder::before,
.mobile-only
  .vjs-play-control.vjs-control.vjs-button.vjs-playing
  .vjs-icon-placeholder::before {
  @apply text-[54px] leading-[54px];
}
.mobile-only .vjs-play-control.vjs-control.vjs-button.vjs-paused,
.mobile-only .vjs-play-control.vjs-control.vjs-button.vjs-playing {
  left: calc(50% - 25px);
  top: calc(48% - 14px);
  @apply fixed w-[54px]  h-[54px] focus:!shadow-none focus-visible:!shadow-none focus-within:!outline-0 focus-visible:!outline-0 focus:!border-0 focus:!outline-0 focus-visible:!border-0;
}
.mobile-only .vjs-skip-backward-10.vjs-control.vjs-button {
  @apply fixed top-[48%] left-1/4 h-[30px];
}
.mobile-only .vjs-skip-forward-10.vjs-control.vjs-button {
  @apply fixed top-[48%] right-1/4 h-[30px];
}

.mobile-only
  .vjs-control-bar
  .vjs-volume-panel
  .vjs-mute-control.vjs-control.vjs-button
  .vjs-icon-placeholder::before,
.mobile-only
  .vjs-control-bar
  .vjs-picture-in-picture-control
  .vjs-icon-placeholder::before,
.mobile-only
  .vjs-playback-rate.vjs-menu-button.vjs-menu-button-popup.vjs-control.vjs-button
  .vjs-icon-placeholder::before,
.mobile-only
  .vjs-fullscreen-control.vjs-control.vjs-button
  .vjs-icon-placeholder::before {
  @apply !text-[25px] !leading-[25px];
}

/* pip handle */

.mobile-only
  .vjs-control-bar
  .vjs-picture-in-picture-control.vjs-control.vjs-button {
  @apply fixed left-16 top-[25px] !h-[25px] !w-[25px];
}
.mobile-only
  .vjs-control-bar
  .vjs-picture-in-picture-control.vjs-control.vjs-button.locked {
  @apply !left-5;
}

/* volume  */
.mobile-only
  .vjs-control-bar
  .vjs-volume-panel.vjs-control.vjs-volume-panel-horizontal
  .vjs-mute-control {
  @apply !h-[25px] !w-[25px];
}
.mobile-only
  .vjs-control-bar
  .vjs-volume-panel.vjs-control.vjs-volume-panel-horizontal
  .vjs-volume-level
  .vjs-icon-placeholder {
  @apply top-[1px];
}
.video-js .vjs-volume-panel .vjs-volume-control.vjs-volume-horizontal {
  @apply !h-auto !hidden;
}
.mobile-only
  .vjs-control-bar
  .vjs-volume-panel.vjs-control.vjs-volume-panel-horizontal {
  @apply !fixed !flex items-center justify-center gap-1.5 flex-row-reverse !top-[25px] !bottom-auto !h-[25px] !w-auto !right-auto !left-5;
}

/* full screen */

.mobile-only .vjs-fullscreen-control.vjs-control.vjs-button {
  @apply fixed !bottom-[70px] !right-5 !h-[25px] !w-[25px];
}

.mobile-only
  .vjs-playback-rate.vjs-menu-button.vjs-menu-button-popup.vjs-control.vjs-button {
  @apply fixed !bottom-[70px] !right-16 !h-[25px] !w-[25px];
}
.mobile-only
  .vjs-playback-rate.vjs-menu-button.vjs-menu-button-popup.vjs-control.vjs-button
  .vjs-playback-rate-value {
  @apply !leading-normal;
}

.mobile-only .vjs-progress-control.vjs-control {
  @apply !bottom-10 px-2.5;
}
.mobile-only .vjs-remaining-time.vjs-time-control.vjs-control {
  @apply fixed bottom-7 !h-5 justify-center items-center flex right-5 !px-0;
}

/* play track */
.mobile-only .video-js .vjs-play-progress .vjs-icon-placeholder {
  @apply !top-[-3.2px];
}
.mobile-only .video-js .vjs-progress-control:hover .vjs-icon-placeholder {
  @apply !top-[-5.2px];
}
</style>